import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, Search, MoreVertical, Pencil, Copy, Trash2, X, Loader2, CheckCircle2, HelpCircle } from "lucide-react";
import { toast } from "sonner";

import { confirmar } from "@/components/ConfirmDialog";
const errMsg = (e) => formatApiError(e.response?.data?.detail);
export const QTYPES = { unica: "Múltipla escolha (1 correta)", multipla: "Várias corretas", vf: "Verdadeiro ou falso" };
const blankOpts = () => [0, 1, 2, 3].map(() => ({ id: "", text: "", correct: false }));

/** Banco de questões do curso. */
export function QuestionBank({ course }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [mod, setMod] = useState("");
  const [dlg, setDlg] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get(`/courses/${course.id}/questions`).then((r) => setList(r.data)).catch(() => setList([])), [course.id]);
  useEffect(() => { load(); }, [load]);

  const modName = (id) => course.modules.find((m) => m.id === id)?.title || "";
  const shown = useMemo(() => (list || []).filter((x) => (!mod || x.module_id === mod) && (!q || x.statement.toLowerCase().includes(q.toLowerCase()))), [list, q, mod]);

  const openNew = () => setDlg({ statement: "", type: "unica", options: blankOpts(), explanation: "", module_id: mod });
  const openEdit = (x) => setDlg({ ...x, options: x.type === "vf" ? x.options : [...x.options] });
  const setOpt = (i, patch) => setDlg((p) => {
    let options = p.options.map((o, j) => (j === i ? { ...o, ...patch } : o));
    if (patch.correct && p.type !== "multipla") options = options.map((o, j) => ({ ...o, correct: j === i }));
    return { ...p, options };
  });
  const setType = (type) => setDlg((p) => ({
    ...p, type,
    options: type === "vf" ? [{ id: "", text: "Verdadeiro", correct: true }, { id: "", text: "Falso", correct: false }]
      : p.type === "vf" ? blankOpts() : type === "unica" ? p.options.map((o, i) => ({ ...o, correct: o.correct && i === p.options.findIndex((x) => x.correct) })) : p.options,
  }));
  const save = async () => {
    setSaving(true);
    try {
      const body = { statement: dlg.statement, type: dlg.type, options: dlg.options, explanation: dlg.explanation, module_id: dlg.module_id || "" };
      if (dlg.id) await api.put(`/courses/${course.id}/questions/${dlg.id}`, body);
      else await api.post(`/courses/${course.id}/questions`, body);
      toast.success(dlg.id ? "Questão atualizada." : "Questão criada.");
      setDlg(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="mt-2">
      <p className="text-sm text-slate-500">Questões deste curso. A prova final usa as questões daqui.</p>
      <div className="flex flex-wrap gap-2 items-center mt-4 mb-3">
        <select value={mod} onChange={(e) => setMod(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm min-w-[200px]">
          <option value="">Todos os módulos</option>
          {course.modules.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar questões..." className="pl-9 h-10" />
        </div>
        <Button onClick={openNew} className="text-white" style={{ background: "#1E9E2A" }} data-testid="btn-new-question"><Plus className="h-4 w-4 mr-1.5" /> Nova questão</Button>
      </div>

      <div className="overflow-x-auto iep-scroll rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50/60">
            <th className="px-4 py-3 w-12">#</th><th className="px-3 py-3">Enunciado</th><th className="px-3 py-3 hidden md:table-cell">Tipo</th>
            <th className="px-3 py-3 hidden lg:table-cell">Módulo</th><th className="px-4 py-3 text-right">Opções</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {list === null && <tr><td colSpan={5} className="px-4 py-8 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 inline" /></td></tr>}
            {list && shown.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">{list.length ? "Nenhuma questão com esse filtro." : "Nenhuma questão ainda. Clique em Nova questão."}</td></tr>}
            {shown.map((x) => (
              <tr key={x.id} data-testid={`question-${x.id}`}>
                <td className="px-4 py-2.5 text-slate-500">{(list || []).indexOf(x) + 1}</td>
                <td className="px-3 py-2.5"><button onClick={() => openEdit(x)} className="text-left text-slate-800 hover:text-[#1E9E2A] line-clamp-2">{x.statement}</button>
                  <div className="text-xs text-slate-400">{x.options.length} alternativas · {x.options.filter((o) => o.correct).length} correta(s)</div></td>
                <td className="px-3 py-2.5 hidden md:table-cell text-slate-600 whitespace-nowrap">{QTYPES[x.type]}</td>
                <td className="px-3 py-2.5 hidden lg:table-cell text-slate-600">{modName(x.module_id) || "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild><button className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => openEdit(x)}><Pencil className="h-4 w-4 mr-2" /> Editar</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => act(() => api.post(`/courses/${course.id}/questions/${x.id}/copy`), "Questão duplicada.")}><Copy className="h-4 w-4 mr-2" /> Duplicar</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-600" onSelect={async () => (await confirmar("Excluir esta questão?")) && act(() => api.delete(`/courses/${course.id}/questions/${x.id}`), "Questão excluída.")}><Trash2 className="h-4 w-4 mr-2" /> Excluir</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {list && <p className="text-xs text-slate-400 mt-2">Total de {list.length} questão(ões) no banco.</p>}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto iep-scroll">
          <DialogHeader><DialogTitle className="font-display">{dlg?.id ? "Editar questão" : "Nova questão"}</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-4 py-2">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Tipo</Label>
                  <select value={dlg.type} onChange={(e) => setType(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="question-type">
                    {Object.entries(QTYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select></div>
                <div className="space-y-1.5"><Label>Módulo (opcional)</Label>
                  <select value={dlg.module_id} onChange={(e) => setDlg((p) => ({ ...p, module_id: e.target.value }))} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                    <option value="">—</option>{course.modules.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
                  </select></div>
              </div>
              <div className="space-y-1.5"><Label><span className="text-red-500 mr-1">*</span>Enunciado</Label>
                <Textarea rows={3} value={dlg.statement} onChange={(e) => setDlg((p) => ({ ...p, statement: e.target.value }))} placeholder="Escreva a pergunta" data-testid="question-statement" /></div>
              <div className="space-y-2">
                <Label><span className="text-red-500 mr-1">*</span>{dlg.type === "vf" ? "A afirmação é:" : `Alternativas — marque ${dlg.type === "multipla" ? "todas as corretas" : "a correta"}`}</Label>
                {dlg.options.map((o, i) => (
                  <div key={i} className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${o.correct ? "border-green-300 bg-green-50" : "border-slate-200"}`}>
                    {dlg.type === "multipla"
                      ? <Checkbox checked={o.correct} onCheckedChange={(v) => setOpt(i, { correct: !!v })} />
                      : <input type="radio" name="correct" checked={o.correct} onChange={() => setOpt(i, { correct: true })} className="h-4 w-4 accent-[#1E9E2A]" />}
                    <span className="text-xs font-bold text-slate-400 w-5">{String.fromCharCode(65 + i)}</span>
                    {dlg.type === "vf" ? <span className="flex-1 text-sm py-1.5">{o.text}</span> : (
                      <Input value={o.text} onChange={(e) => setOpt(i, { text: e.target.value })} placeholder={`Alternativa ${String.fromCharCode(65 + i)}`} className="border-0 shadow-none focus-visible:ring-0 bg-transparent" data-testid={`question-option-${i}`} />
                    )}
                    {o.correct && <CheckCircle2 className="h-4 w-4 text-[#1E9E2A] shrink-0" />}
                    {dlg.type !== "vf" && dlg.options.length > 2 && (
                      <button type="button" onClick={() => setDlg((p) => ({ ...p, options: p.options.filter((_, j) => j !== i) }))} className="text-slate-300 hover:text-red-500"><X className="h-4 w-4" /></button>
                    )}
                  </div>
                ))}
                {dlg.type !== "vf" && dlg.options.length < 8 && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setDlg((p) => ({ ...p, options: [...p.options, { id: "", text: "", correct: false }] }))}><Plus className="h-4 w-4 mr-1" /> Alternativa</Button>
                )}
              </div>
              <div className="space-y-1.5"><Label className="flex items-center gap-1"><HelpCircle className="h-3.5 w-3.5" /> Explicação (opcional, aparece no gabarito)</Label>
                <Textarea rows={2} value={dlg.explanation} onChange={(e) => setDlg((p) => ({ ...p, explanation: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="text-white" style={{ background: "#1E9E2A" }} data-testid="question-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar questão"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
